import Link from "next/link";
import { formatEventDate, formatLocation, formatEventType } from "@/lib/events";
import type { RowingEvent } from "@/types";

interface EventCardProps {
  event: RowingEvent;
}

export function EventCard({ event }: EventCardProps) {
  return (
    <div className="rounded-lg border border-gray-200 p-5 transition-colors hover:border-primary dark:border-gray-700 dark:hover:border-primary-light">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <Link
            href={`/events/${event.slug}`}
            className="font-semibold text-gray-900 hover:text-primary dark:text-gray-100"
          >
            {event.name}
          </Link>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {event.description}
          </p>
        </div>
        <span className="shrink-0 rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-200">
          {formatEventType(event.eventType)}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
        <span className="flex items-center gap-1">
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          {formatEventDate(event)}
        </span>
        <span className="flex items-center gap-1">
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          {formatLocation(event.location)}
        </span>
      </div>

      {event.boatClasses.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {event.boatClasses.map((bc) => (
            <span
              key={bc}
              className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-300"
            >
              {bc}
            </span>
          ))}
        </div>
      )}

      <div className="mt-3 flex items-center gap-3">
        <Link
          href={`/events/${event.slug}`}
          className="text-sm font-medium text-primary hover:underline"
        >
          Details
        </Link>
        <a
          href={event.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-gray-500 hover:text-primary dark:text-gray-400"
        >
          Visit site &rarr;
        </a>
      </div>
    </div>
  );
}
